import { useEffect, useState } from "react";

const SDK_KEY = process.env.REACT_APP_STATSIG_SDK_KEY ?? "";
const ENVIRONMENT = process.env.REACT_APP_APP_ENV;

const LINE_HAUL_NETWORKS_GATE = "line_haul_networks";

/**
 * Checks the Statsig `line_haul_networks` feature gate (backend enum
 * `FF_LINE_HAUL_NETWORKS`) for the given company.
 *
 * Mirrors the inline Statsig `check_gate` fetch pattern used by the Crown
 * tariff importer: it calls the Statsig HTTP API directly rather than standing
 * up the SDK for a single gate. The fetch runs only when `companyUuid` is a
 * non-empty string. On error or while the request is in flight, `enabled`
 * defaults to `false`.
 *
 * @param companyUuid The company UUID to evaluate the gate for. An empty string
 *   skips the fetch and leaves `enabled` as `false`.
 * @returns An object with `enabled` (whether the gate passed) and `loading`
 *   (whether the gate check is currently in flight).
 */
export const useLineHaulNetworksEnabled = (
  companyUuid: string,
): { enabled: boolean; loading: boolean } => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (companyUuid === "") {
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://api.statsig.com/v1/check_gate", {
          method: "POST",
          headers: {
            "statsig-api-key": SDK_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user: {
              userID: "",
              custom: { companyUuid },
              statsigEnvironment: { tier: ENVIRONMENT },
            },
            gateNames: [LINE_HAUL_NETWORKS_GATE],
          }),
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data: unknown = await response.json();
        const gateValue = (
          data as Record<string, { value?: boolean } | undefined>
        )?.[LINE_HAUL_NETWORKS_GATE]?.value;
        setEnabled(gateValue ?? false);
      } catch {
        setEnabled(false);
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, [companyUuid]);

  return { enabled, loading };
};
